'use client';

import {IMGPW} from '@/config';
import {useTranslations} from 'next-intl';
import {useEffect, useMemo} from 'react';
import useShop from '../../../../../stores/shop/useContext';
import EmptyState from '../commons/EmptyState';
import ProductList from '../commons/ProductList';
import {useFindFacetsQuery} from '../query/useFindFacetsQuery';
import useFindManyProductsQuery from '../query/useFindManyProductsQuery';
import ActiveFilters from './ActiveFilters';
import DiscountedProducts from './DiscountedProducts';
import FilterList from './FilterList';
import LocationAlerts from './LocationAlerts';
import useFilters from './hooks/useFilters';
import useShopTracking from './hooks/useShopTracking';

export default function Shop() {
  const t = useTranslations('shop');

  const {updateFacets} = useShop((state) => state);
  const {filters} = useFilters();

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
  } = useFindManyProductsQuery(filters);

  const products = useMemo(() => {
    return (data?.pages || []).reduce((acc, curr) => {
      return [...acc, ...curr.items];
    }, []);
  }, [data]);

  useShopTracking(products);

  const {data: facets} = useFindFacetsQuery();

  useEffect(() => {
    updateFacets(facets);
  }, [facets]);

  return (
    <div className="bg-white sm:bg-greyscale-background">
      <div className="container mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-8 py-6 px-4 sm:py-8 lg:px-6 xl:px-8">
        <div className="hidden md:block">
          <FilterList />
        </div>
        <div className="sm:col-span-2 lg:col-span-3 xl:col-span-4">
          <LocationAlerts />
          <div className="space-y-6">
            <ActiveFilters />
            {!(isFetching || isFetchingNextPage) && products.length === 0 && (
              <EmptyState
                imageUrl={IMGPW + '/error.png'}
                title={t('noResults.title')}
                body={t('noResults.title')}
              />
            )}
            <DiscountedProducts />
            <div className="sm:space-y-6">
              {products.length > 0 && (
                <h1 className="flex items-center gap-2 text-3xl text-greyscale-title font-bold tracking-tighter leading-8 m-0 p-0">
                  {t('main.title')}
                </h1>
              )}
              <ProductList
                columns={{
                  xl: 4,
                  lg: 3,
                  md: 2,
                  sm: 2,
                  xs: 1,
                }}
                loading={isFetching || isFetchingNextPage}
                hasNextPage={hasNextPage}
                onLoadMore={fetchNextPage}
                products={products}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
